/**
 * layoutConfigStore — loads and persists per-layout configuration from localStorage.
 *
 * Each layout (L1–L12 + custom) has a LayoutConfig that controls what
 * elements are enabled by default when that layout is used in a scene.
 * buildSceneProps reads these configs to apply them automatically.
 */
import type { LayoutConfig, SceneLayoutId } from '../types';

const STORAGE_KEY = 'buzz24_layout_configs_v2';

// ─── Defaults ────────────────────────────────────────────────

function makeDefault(
  id: SceneLayoutId,
  label: string,
  description: string,
  overrides: Partial<LayoutConfig> = {},
): LayoutConfig {
  return {
    id,
    baseLayoutId: id,
    label,
    description,
    screenStyle: 'full-screen',
    showImage: true,
    showVideo: true,
    videoMuted: false,
    preferVideo: false,
    voiceoverEnabled: true,
    bgMusicEnabled: true,
    showHeadline: true,
    showSubheadline: true,
    showBigNumber: false,
    showQuote: false,
    showHighlightWords: false,
    showTicker: true,
    showLogo: true,
    showTopBar: true,
    showStoryCounter: true,
    showBreakingBanner: false,
    defaultDuration: 8,
    ...overrides,
  };
}

export const DEFAULT_LAYOUT_CONFIGS: LayoutConfig[] = [
  makeDefault('L1', 'Full Screen Visual', 'Image or video fills the entire frame with headline overlay', {
    screenStyle: 'full-screen', showBigNumber: false,
  }),
  makeDefault('L2', 'Visual Left · Text Right', 'Media on left half, text on right half', {
    screenStyle: 'split-lr', showSubheadline: true,
  }),
  makeDefault('L3', 'Text Left · Visual Right', 'Text on left half, media on right half', {
    screenStyle: 'split-lr', showSubheadline: true,
  }),
  makeDefault('L4', 'Two Visual Split', 'Two media panels side by side with headline below', {
    screenStyle: 'split-lr', showSubheadline: false,
  }),
  makeDefault('L5', 'Full Screen Headline', 'Bold headline on solid/blurred background', {
    screenStyle: 'text-focus', showImage: false, showVideo: false,
    showSubheadline: true, defaultDuration: 5,
  }),
  makeDefault('L6', 'Visual + Bottom Headline', 'Full visual on top, headline bar at bottom', {
    screenStyle: 'full-screen',
  }),
  makeDefault('L7', 'Big Number Graphic', 'Prominent statistic or number display', {
    screenStyle: 'graphic', showBigNumber: true, showImage: true,
    showVideo: false, showSubheadline: false, defaultDuration: 6,
  }),
  makeDefault('L8', 'Quote Layout', 'Person photo with pull-quote text', {
    screenStyle: 'split-lr', showQuote: true, showSubheadline: false,
  }),
  makeDefault('L10', 'Multi-image Grid', 'Grid of 2–4 images with headline', {
    screenStyle: 'grid', showVideo: false,
  }),
  makeDefault('L12', 'Text Highlight', 'Key words highlighted in large animated text', {
    screenStyle: 'text-focus', showHighlightWords: true, showImage: true,
    showVideo: false, defaultDuration: 6,
  }),
  makeDefault('L13', 'Lower Third', 'Broadcast name/title bar slides in from left over full-screen media', {
    screenStyle: 'full-screen', showSubheadline: true, defaultDuration: 7,
  }),
];

// ─── Persistence ──────────────────────────────────────────────

export function loadLayoutConfigs(): LayoutConfig[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [...DEFAULT_LAYOUT_CONFIGS];
    const stored = JSON.parse(raw) as LayoutConfig[];
    // Merge: keep stored overrides, add any new defaults that aren't stored yet
    const storedIds = new Set(stored.map((c) => c.id));
    const missing = DEFAULT_LAYOUT_CONFIGS.filter((d) => !storedIds.has(d.id));
    return [...stored, ...missing];
  } catch {
    return [...DEFAULT_LAYOUT_CONFIGS];
  }
}

export function saveLayoutConfigs(configs: LayoutConfig[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(configs));
  } catch { /* storage full — silently ignore */ }
}

export function resetLayoutConfigs(): LayoutConfig[] {
  localStorage.removeItem(STORAGE_KEY);
  return [...DEFAULT_LAYOUT_CONFIGS];
}

export function getLayoutConfig(id: string, configs: LayoutConfig[]): LayoutConfig | undefined {
  return configs.find((c) => c.id === id);
}

export function upsertLayoutConfig(config: LayoutConfig, configs: LayoutConfig[]): LayoutConfig[] {
  const exists = configs.some((c) => c.id === config.id);
  const updated = exists
    ? configs.map((c) => c.id === config.id ? config : c)
    : [...configs, config];
  saveLayoutConfigs(updated);
  return updated;
}

export function deleteLayoutConfig(id: string, configs: LayoutConfig[]): LayoutConfig[] {
  const updated = configs.filter((c) => c.id !== id);
  saveLayoutConfigs(updated);
  return updated;
}

/**
 * Apply a LayoutConfig's defaults to a partial SceneProps override object.
 * Called from buildSceneProps — settings that come from LayoutConfig
 * are used unless the story's specific data overrides them.
 */
export function layoutConfigToSceneOverrides(config: LayoutConfig): {
  hideImage: boolean;
  hideVideo: boolean;
  videoMuted: boolean;
  showTicker: boolean;
  showLogo: boolean;
  showTopBar: boolean;
  showStoryCounter: boolean;
  overlayGeometry: LayoutConfig['overlayGeometry'];
} {
  return {
    hideImage: !config.showImage,
    hideVideo: !config.showVideo,
    videoMuted: config.videoMuted,
    showTicker: config.showTicker,
    showLogo: config.showLogo,
    showTopBar: config.showTopBar,
    showStoryCounter: config.showStoryCounter,
    overlayGeometry: config.overlayGeometry,
  };
}
